/** @format */
import { css } from 'styled-components';

import { newActionHighlight } from '../new-action-pulse';

/**
 * Sits beside the product name rather than inside it, which is what lets the
 * icon keep its own size: feather draws a glyph inside a circle at fixed
 * proportions, and scaling that from the outside — an em box against whatever
 * font-size it happened to inherit — stretches one against the other. The row
 * this sits in lines it up with the name; the size comes from the icon's own
 * size prop. @see views/product/product.styles.tsx
 *
 * There is no visible label here, unlike the model page's hero action, so the
 * pulse marking this as new goes on the button itself rather than on a child.
 *
 * The button carries no border or background of its own — it reads as the icon
 * and nothing else — and removing the browser's default focus ring is only safe
 * because `:focus-visible` replaces it with one drawn from a theme colour, per
 * the accessibility rule against removing an outline without a replacement.
 */
export const styles = css`
    ${newActionHighlight}

    /*
     * How far the icon is lifted from where inline alignment leaves it.
     *
     * Tuned by eye against the heading rather than derived from the font's
     * metrics: the button is a zero-line-height inline-flex box beside a heavy
     * display face, and where that lands is not something cap height or x-height
     * predicts. Chosen at the size the product name is set in, and in em so it
     * holds as that size changes by breakpoint.
     */
    --insights-action__lift: 0.5em;

    display: inline-flex;
    align-items: center;
    justify-content: center;
    /*
     * Aligned to the text by eye rather than to its baseline: a circle sitting on
     * a baseline beside capitals reads as having slipped downwards, and the middle
     * keyword only gets part of the way there, since it centres on the lowercase
     * midline while the name beside it is set in capitals. The lift covers the
     * rest. @see the property above for why it is not a calculation.
     */
    vertical-align: middle;
    transform: translateY(calc(-1 * var(--insights-action__lift)));
    margin-left: var(--layout-spacing-8);
    padding: 0;
    border: none;
    background: none;
    cursor: pointer;
    line-height: 0;

    &:focus-visible {
        outline: 0.2rem solid var(--colors-brand-secondary);
        outline-offset: 0.2rem;
    }
`;
